import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { api_urls } from "../../utilities";
import {
  getAgentService,
  getService,
  postAgentService,
  postService,
} from "../services";

function normalizeAppointmentList(inner) {
  if (inner == null) return [];
  if (Array.isArray(inner)) return inner;
  return (
    inner.data ??
    inner.appointments ??
    inner.records ??
    inner.list ??
    []
  );
}

export const fetchAppointments = createAsyncThunk(
  "appointment/fetchAppointments",
  async (_, { getState, rejectWithValue }) => {
    try {
      const useAgent = getState().auth?.portal === "agent";
      const response = useAgent
        ? await getAgentService(api_urls.appointments, {})
        : await getService(api_urls.appointments, {});
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      const inner = envelope.data ?? envelope;
      return normalizeAppointmentList(inner);
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const createAppointment = createAsyncThunk(
  "appointment/create",
  async (payload, { getState, dispatch, rejectWithValue }) => {
    try {
      const { quote_request_id, ...body } = payload ?? {};
      if (!quote_request_id) {
        return rejectWithValue({
          httpStatus: 400,
          body: { message: "Quote request is required" },
        });
      }

      const useAgent = getState().auth?.portal === "agent";
      const finalizePath = useAgent
        ? api_urls.agentQuoteFinalize(quote_request_id)
        : api_urls.quoteRequestFinalize(quote_request_id);
      const response = useAgent
        ? await postAgentService(finalizePath, body)
        : await postService(finalizePath, body);
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      dispatch(fetchAppointments());
      return envelope.data ?? envelope;
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

export const setAppointmentStatus = createAsyncThunk(
  "appointment/setStatus",
  async ({ id, status }, { getState, dispatch, rejectWithValue }) => {
    const useAgent = getState().auth?.portal === "agent";
    const path =
      status === "cancelled"
        ? api_urls.appointmentCancel(id)
        : api_urls.appointmentComplete(id);

    try {
      const response = useAgent
        ? await postAgentService(path, {})
        : await postService(path, {});
      const envelope = response.data ?? {};
      const code = envelope.code ?? envelope.status;
      if (typeof code === "number" && code !== 200) {
        return rejectWithValue({
          httpStatus: response.status,
          body: envelope,
        });
      }
      dispatch(fetchAppointments());
      return { id, status, data: envelope.data ?? envelope };
    } catch (error) {
      return rejectWithValue({
        httpStatus: error?.status,
        body: error?.data ?? { message: error?.message },
      });
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  creating: false,
  statusUpdating: false,
};

export const appointmentSlice = createSlice({
  name: "appointment",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAppointments.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = Array.isArray(payload) ? payload : [];
      })
      .addCase(fetchAppointments.rejected, (state) => {
        state.loading = false;
      })
      .addCase(setAppointmentStatus.pending, (state) => {
        state.statusUpdating = true;
      })
      .addCase(setAppointmentStatus.fulfilled, (state) => {
        state.statusUpdating = false;
      })
      .addCase(setAppointmentStatus.rejected, (state) => {
        state.statusUpdating = false;
      })
      .addCase(createAppointment.pending, (state) => {
        state.creating = true;
      })
      .addCase(createAppointment.fulfilled, (state) => {
        state.creating = false;
      })
      .addCase(createAppointment.rejected, (state) => {
        state.creating = false;
      });
  },
});

export default appointmentSlice.reducer;
